import {
    isValidElement,
    Children,
    useMemo,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IBubbleProps from "./type";
import type {
    BubbleContextType
} from "./type";
import stylesheet, {
    BUBBLE_QUICK_REACTIONS,
    getBubbleColors,
    getBubbleSize,
    useStyles
} from "./stylesheet";
import BubbleContent from "./components/content";
import BubbleGroup from "./components/group";
import BubbleReactions from "./components/reactions";
import BubbleTyping from "./components/typing";
import {
    BubbleContext
} from "./context";
import {
    NCoreUIKitTheme
} from "../../core/hooks";

const BUBBLE_EMPTY_REACTIONS: NonNullable<IBubbleProps["reactions"]> = [];

const Bubble: FC<IBubbleProps> = ({
    quickReactions = BUBBLE_QUICK_REACTIONS,
    reactions = BUBBLE_EMPTY_REACTIONS,
    isDefaultExpanded = false,
    reactionsSide = "bottom",
    variant = "assistant",
    contentType = "text",
    isCollapsible = true,
    isStreaming = false,
    isDisabled = false,
    onReactionsPress,
    size = "medium",
    customLocalize,
    reactionsTitle,
    onReactionAdd,
    customTheme,
    children,
    onPress,
    style,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        activeTheme,
        typography,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const currentSize = useMemo(() => getBubbleSize({
        controlPaddings,
        controlHeights,
        typography,
        size
    }), [
        controlPaddings,
        controlHeights,
        typography,
        size
    ]);

    const isDarkTheme = activeTheme === "dark";

    const currentColors = useMemo(() => getBubbleColors({
        isPressable: !!onPress,
        isDarkTheme,
        isDisabled,
        variant,
        colors
    }), [
        isDarkTheme,
        isDisabled,
        onPress,
        variant,
        colors
    ]);

    const {
        container: containerDynamicStyle
    } = useStyles({
        variant
    });

    const context: BubbleContextType = useMemo(() => ({
        isDefaultExpanded,
        onReactionsPress,
        customLocalize,
        quickReactions,
        reactionsTitle,
        currentColors,
        isCollapsible,
        onReactionAdd,
        reactionsSide,
        contentType,
        currentSize,
        customTheme,
        isStreaming,
        isDisabled,
        reactions,
        onPress,
        variant,
        size
    }), [
        isDefaultExpanded,
        onReactionsPress,
        quickReactions,
        onReactionAdd,
        customLocalize,
        reactionsTitle,
        currentColors,
        isCollapsible,
        reactionsSide,
        contentType,
        currentSize,
        customTheme,
        isStreaming,
        isDisabled,
        reactions,
        onPress,
        variant,
        size
    ]);

    const isCompound = Children.toArray(children).some((child) => {
        return isValidElement(child) && (child.type === BubbleContent || child.type === BubbleReactions);
    });

    const renderReactions = () => {
        if(reactions.length === 0 && !onReactionAdd) {
            return null;
        }

        return <BubbleReactions />;
    };

    const renderDefaultLayout = () => {
        return <>
            {reactionsSide === "top" ? renderReactions() : null}
            <BubbleContent>
                {children}
            </BubbleContent>
            {reactionsSide === "bottom" ? renderReactions() : null}
        </>;
    };

    return <BubbleContext.Provider
        value={context}
    >
        <View
            {...props}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            aria-busy={isStreaming}
        >
            {isCompound ? children : renderDefaultLayout()}
        </View>
    </BubbleContext.Provider>;
};
export default Object.assign(Bubble, {
    Reactions: BubbleReactions,
    Content: BubbleContent,
    Typing: BubbleTyping,
    Group: BubbleGroup
});
